import { motion } from "framer-motion";
import { Flame, ShoppingCart, Star, PilcrowSquareIcon, Pill } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

export default function ActiveTests() {
  const user_id = localStorage.getItem("userId");
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  // Fetch bookings whenever page changes
  useEffect(() => {
    let cancelled = false;

    const fetchBookings = async () => {
      if (!hasMore || !user_id) return;
      setLoading(true);
      try {
        const res = await api.get(
          `/api/lab-bookings/user/${user_id}?page=${page}&limit=10`
        );
        if (cancelled) return;

        const newBookings = res.data.bookings || [];

        setTrending((prev) =>
          page === 1 ? newBookings : [...prev, ...newBookings]
        );

        // If server returned fewer than a full page, we're done
        if (newBookings.length < 10) {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching bookings:", err);
        if (!cancelled) setHasMore(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBookings();

    return () => {
      cancelled = true;
    };
  }, [page, user_id]);

  // Infinite scroll observer
  useEffect(() => {
    const current = loaderRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "100px" }
    );

    observer.observe(current);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  return (
    <section className="container" style={styles.page}>
      {/* Header */}
      <FeatureHeader
        title="Bookings"
        subtitle="All your lab tests booked"
        onBack={() => navigate(-1)}
      />

      {/* Test Grid */}
      <div style={styles.grid}>
        {trending.length === 0 && !loading ? (
          <p className="text-muted">No active tests.</p>
        ) : (
          [...trending].reverse().map((b) => {
            const test = b.tests[0];
            const status = test?.result?.status || "Processing";

            return (
              <div
                key={b._id}
                onClick={() => navigate(`/booking-details/${b._id}`)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  marginBottom: 10,
                  borderRadius: 16,
                  backgroundColor: "#fff",
                  border: "1px solid #eee",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                  cursor: "pointer",
                }}
              >
                {/* Left Icon */}
                <div
                  style={{
                    flexShrink: 0,
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    backgroundColor: "#F4F1FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={test?.testId?.image}
                    alt=""
                    style={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                </div>

                {/* Middle Content */}
                <div
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      lineHeight: 1.3,
                      color: "#1a1a1a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={test?.testId?.name || "Unnamed Test"}
                  >
                    {test?.testId?.name || "Unnamed Test"}
                  </div>

                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    Est. {status === "Completed" ? "Completed" : "24–48 hrs"}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#1a1a1a",
                    }}
                  >
                    ${b.totalAmount?.toLocaleString()}
                  </div>
                </div>

                {/* Right Actions */}
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      padding: "4px 8px",
                      borderRadius: 20,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      backgroundColor:
                        status === "Completed" ? "#E6F7F1" : "#FFF4E5",
                      color: status === "Completed" ? "#1E9E6A" : "#C77700",
                    }}
                  >
                    {status === "Completed" ? "Completed" : "Processing"}
                  </div>

                  {status === "Completed" && (
                    <button
                      style={{
                        backgroundColor: "#6A3AFF",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Book Test
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Loader */}
      <div ref={loaderRef} style={{ textAlign: "center", padding: 20 }}>
        {loading && <Loader />}
        {!hasMore && trending.length > 0 && (
          <span style={{ color: "#888", fontSize: 13 }}>No more bookings</span>
        )}
      </div>
    </section>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "var(--bg)",
    fontFamily: "'Poppins', sans-serif",
    position: "relative",
  },
  backButton: {
    position: "fixed",
    top: 20,
    left: 16,
    cursor: "pointer",
    fontSize: "20px",
    background: "rgba(255,255,255,0.2)",
    padding: "12px",
    borderRadius: "50%",
    transition: "background 0.2s ease",
    color: "#333",
    zIndex: 100,
  },
  header: {
    marginBottom: 20,
    textAlign: "left",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2e1065",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
    width: "100%",
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  imageWrapper: {
    position: "relative",
    width: "100%",
    height: 110,
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  prescriptionBadge: {
    position: "absolute",
    top: 8,
    left: 8,
    color: "#fff",
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 12,
    padding: "3px 8px",
    backdropFilter: "blur(4px)",
  },
  info: {
    padding: "8px 10px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  name: {
    fontSize: 11.5,
    fontWeight: 600,
    color: "#2d2d2d",
    margin: "4px 0 6px 0",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  priceRow: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginBottom: 4,
  },
  price: {
    fontSize: 12,
    fontWeight: 700,
    color: "#7e57c2",
  },
  oldPrice: {
    fontSize: 10,
    textDecoration: "line-through",
    color: "#9ca3af",
  },
  rating: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10.5,
    color: "#6b7280",
    marginBottom: 6,
  },
  ratingText: {
    fontWeight: 500,
  },
  reviewsText: {
    color: "#9ca3af",
    fontSize: 10,
  },
  orderButton: {
    background: "linear-gradient(90deg, #8e2de2 0%, #4a00e0 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(126,87,194,0.25)",
    width: "100%",
  },
  skeletonCard: {
    height: 190,
    borderRadius: 16,
    background:
      "linear-gradient(90deg, #f2f2f2 25%, #e0e0e0 50%, #f2f2f2 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
  },
};